import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Copy, ArrowLeft, Save, ExternalLink, Mic, FileText, Paperclip } from "lucide-react";

const StatusBadge = ({ status }) => {
  const map = {
    completed: { label: "Terminée", cls: "status-completed" },
    in_progress: { label: "En cours", cls: "status-inprogress" },
    not_started: { label: "À faire", cls: "status-notstarted" },
  };
  const m = map[status] || map.not_started;
  return <span className={`dec-pill px-2.5 py-0.5 text-xs ${m.cls}`}>{m.label}</span>;
};

const subIcon = (kind) => kind === "audio" ? Mic : kind === "file" ? Paperclip : FileText;
const subLabel = (kind) => kind === "audio" ? "Audio" : kind === "file" ? "Fichier" : "Note";

export default function LearnerProfile() {
  const { id } = useParams();
  const [learner, setLearner] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [missions, setMissions] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [l, s, m, sub] = await Promise.all([
      api.get(`/coach/learners/${id}`),
      api.get(`/coach/learners/${id}/sessions`),
      api.get(`/coach/missions`, { params: { learner_id: id } }),
      api.get(`/coach/submissions`, { params: { learner_id: id } }),
    ]);
    setLearner(l.data); setSessions(s.data); setMissions(m.data); setSubmissions(sub.data);
    setForm({
      first_name: l.data.first_name, last_name: l.data.last_name, email: l.data.email,
      phone: l.data.phone, level: l.data.level, notes: l.data.notes, status: l.data.status,
    });
  };
  useEffect(() => { load(); }, [id]);

  if (!learner) return <div className="text-[#5C6561]">Chargement…</div>;

  const completed = sessions.filter(s => s.status === "completed").length;
  const total = sessions.length || 40;
  const missionsDone = missions.filter(m => m.completed).length;

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/coach/learners/${id}`, form);
      toast.success("Apprenant mis à jour"); load();
    } catch { toast.error("Erreur"); }
    finally { setSaving(false); }
  };

  const copyLink = () => {
    const url = `${window.location.origin}/l/${learner.access_token}`;
    navigator.clipboard.writeText(url);
    toast.success("Lien apprenant copié");
  };

  return (
    <div>
      <Link to="/coach/apprenants" className="text-sm text-[#5C6561] inline-flex items-center gap-1 mb-4"><ArrowLeft className="w-4 h-4" /> Retour</Link>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-serif-accent text-3xl text-[#1F2421]">{learner.first_name} {learner.last_name}</h1>
          <div className="text-[#5C6561] text-sm mt-1">{learner.email} • Niveau {learner.level}</div>
        </div>
        <Button data-testid="learner-profile-copy-link" onClick={copyLink} variant="outline" className="dec-pill bg-white">
          <Copy className="w-4 h-4 mr-2" /> Copier le lien privé
        </Button>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mt-6">
        <div className="dec-card p-5">
          <div className="text-xs uppercase tracking-wider text-[#5C6561]">Progression</div>
          <div className="mt-2 text-3xl font-bold text-[#1F2421]">{completed}/{total}</div>
          <Progress value={(completed / total) * 100} className="mt-3" />
        </div>
        <div className="dec-card p-5">
          <div className="text-xs uppercase tracking-wider text-[#5C6561]">Missions</div>
          <div className="mt-2 text-3xl font-bold text-[#1F2421]">{missionsDone}/{missions.length}</div>
        </div>
        <div className="dec-card p-5 dec-side-accent">
          <div className="text-xs uppercase tracking-wider text-[#5C6561]">Programme</div>
          <div className="mt-1 font-semibold">{learner.program}</div>
          <div className="text-xs text-[#5C6561] mt-1">Depuis le {learner.start_date}</div>
        </div>
      </div>

      <Tabs defaultValue="sessions" className="mt-8">
        <TabsList className="bg-[#F4EFEA]">
          <TabsTrigger value="sessions" data-testid="tab-sessions">40 Séances</TabsTrigger>
          <TabsTrigger value="missions" data-testid="tab-missions">Missions ({missions.length})</TabsTrigger>
          <TabsTrigger value="submissions" data-testid="tab-submissions">Travaux ({submissions.length})</TabsTrigger>
          <TabsTrigger value="profile" data-testid="tab-profile">Profil</TabsTrigger>
        </TabsList>

        <TabsContent value="sessions" className="mt-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {sessions.map((s) => (
              <Link key={s.id} to={`/coach/seances/${s.id}`} data-testid={`session-item-${s.number}-card`}
                className="dec-card p-4 hover:border-[#D95D39] transition">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-[#5C6561]">Séance {s.number}</div>
                  <StatusBadge status={s.status} />
                </div>
                <div className="mt-1 font-semibold text-[#1F2421] truncate">{s.title}</div>
                {s.course_date && <div className="text-xs text-[#5C6561] mt-1">📅 {s.course_date}</div>}
              </Link>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="missions" className="mt-4">
          {missions.length === 0 ? (
            <div className="text-[#5C6561] text-sm">Aucune mission. Créez-en depuis la page Missions.</div>
          ) : (
            <div className="space-y-2">
              {missions.map((m) => (
                <div key={m.id} className="dec-card p-4 flex items-center justify-between">
                  <div>
                    <div className="font-semibold">{m.title}</div>
                    <div className="text-xs text-[#5C6561]">{m.type === "before" ? "Avant séance" : "Après séance"}</div>
                  </div>
                  <span className={m.completed ? "status-completed dec-pill px-2 py-0.5 text-xs" : "status-notstarted dec-pill px-2 py-0.5 text-xs"}>
                    {m.completed ? "Terminée" : "À faire"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="submissions" className="mt-4">
          {submissions.length === 0 ? (
            <div className="dec-card p-6 text-[#5C6561] text-sm">Aucun dépôt pour l'instant.</div>
          ) : (
            <div className="space-y-2">
              {submissions.map((s) => {
                const Icon = subIcon(s.kind);
                const mission = missions.find(x => x.id === s.mission_id);
                const sess = sessions.find(x => x.id === s.session_id);
                const url = s.file_path ? `${API_BASE}/files/${s.file_path}` : null;
                return (
                  <div key={s.id} className="dec-card p-4" data-testid={`coach-submission-${s.id}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-[#FDF0EC] text-[#D95D39] flex items-center justify-center shrink-0"><Icon className="w-4 h-4" /></div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs uppercase tracking-wider text-[#5C6561]">
                          {subLabel(s.kind)} • {new Date(s.created_at).toLocaleString("fr-FR")}
                        </div>
                        {(mission || sess) && (
                          <div className="text-xs text-[#5C6561] mt-0.5">
                            {mission && <>Mission : <strong>{mission.title}</strong></>}
                            {mission && sess && " · "}
                            {sess && <>Séance {sess.number} — {sess.title}</>}
                          </div>
                        )}
                        {s.note && <div className="mt-2 text-sm whitespace-pre-wrap">{s.note}</div>}
                        {url && (
                          <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-2 text-xs text-[#D95D39] font-medium">
                            <ExternalLink className="w-3 h-3" /> {s.original_filename || "Ouvrir le fichier"}
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="profile" className="mt-4">
          <div className="dec-card p-6 space-y-4 max-w-xl">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Prénom</Label><Input value={form.first_name || ""} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></div>
              <div><Label>Nom</Label><Input value={form.last_name || ""} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
            </div>
            <div><Label>Email</Label><Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Téléphone</Label><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><Label>Niveau</Label>
                <Select value={form.level} onValueChange={(v) => setForm({ ...form, level: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["A1","A2","B1","B2"].map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Statut</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Actif</SelectItem>
                  <SelectItem value="inactive">Inactif</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Notes</Label><Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <Button data-testid="save-learner-profile" disabled={saving} onClick={save} className="bg-[#D95D39] hover:bg-[#C04E2D] text-white">
              <Save className="w-4 h-4 mr-2" /> Enregistrer
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
